import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const SALON_TOKENS = new Set([
  'friseur',
  'friseurin',
  'salon',
  'haarstudio',
  'hair',
  'coiffeur',
  'barber',
  'team',
  'gmbh',
  'ug',
  'inhaber',
  'inhaberin',
]);

function titleCaseNamePart(part: string): string {
  return part
    .split('-')
    .map((chunk) => {
      if (!chunk) return chunk;
      return chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
    })
    .join('-');
}

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Nur echte Vornamen — kein Salonname, keine Mail-Local-Parts. */
export function sanitizeFirstName(raw?: string | null): string {
  if (!raw) return '';
  const first = String(raw)
    .trim()
    .split(/\s+/)[0]
    ?.replace(/^['’]+|['’]+$/g, '') || '';
  if (!/^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ'’-]{1,23}$/.test(first)) return '';
  if (SALON_TOKENS.has(first.toLowerCase())) return '';
  return titleCaseNamePart(first);
}

export function formatHalloGreeting(firstName?: string | null): string {
  const name = sanitizeFirstName(firstName);
  return name ? `Hallo ${name},` : 'Hallo,';
}

export function htmlAlreadyHasHallo(html: string): boolean {
  const text = String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return /^hallo\b/i.test(text);
}

/** Anrede nach Logo-Header, sonst vor den Body. */
export function injectHalloGreetingHtml(html: string, firstName?: string | null): string {
  const source = String(html || '');
  if (!source || htmlAlreadyHasHallo(source)) return source;
  const block = `<p style="margin:16px 0;font-size:16px;line-height:1.5;">${escapeHtml(formatHalloGreeting(firstName))}</p>`;
  const closeIdx = source.indexOf('</div>');
  if (closeIdx !== -1) {
    return source.slice(0, closeIdx + 6) + block + source.slice(closeIdx + 6);
  }
  return block + source;
}

export function collectCrmRecipientNames(clients: Array<Record<string, any> | null | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const client of clients || []) {
    if (!client) continue;
    const name = sanitizeFirstName(
      client.vorname ||
        client.clientData?.vorname ||
        client.contact?.firstName ||
        client.clientData?.contact?.firstName ||
        client.firstName
    );
    if (!name) continue;
    const emails = [
      client.email,
      client.mailadresse,
      client.clientData?.email,
      client.clientData?.mailadresse,
    ]
      .map((value) => String(value || '').toLowerCase().trim())
      .filter((value) => value.includes('@'));
    for (const email of emails) out[email] = name;
  }
  return out;
}

export function parseExternalLeadNames(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const email = String(key || '').toLowerCase().trim();
    const name = sanitizeFirstName(String(value || ''));
    if (email.includes('@') && name) out[email] = name;
  }
  return out;
}

export function groupRecipientsByFirstName(
  emails: string[],
  nameByEmail: Record<string, string>
): Array<{ firstName: string | null; emails: string[] }> {
  const buckets = new Map<string, { firstName: string | null; emails: string[] }>();
  for (const raw of emails) {
    const email = String(raw || '').toLowerCase().trim();
    if (!email.includes('@')) continue;
    const firstName = sanitizeFirstName(nameByEmail[email]) || null;
    const key = firstName || '';
    const bucket = buckets.get(key);
    if (bucket) bucket.emails.push(email);
    else buckets.set(key, { firstName, emails: [email] });
  }
  return Array.from(buckets.values());
}

export async function persistExternalLeadProfile(email: string, firstName?: string | null): Promise<void> {
  const normalized = String(email || '').toLowerCase().trim();
  if (!normalized.includes('@')) return;
  const payload: Record<string, unknown> = {
    externalLeads: arrayUnion(normalized),
    updatedAt: new Date().toISOString(),
  };
  const name = sanitizeFirstName(firstName);
  if (name) {
    payload.externalLeadNames = { [normalized]: name };
  }
  await setDoc(doc(db, 'newsletter_system', 'global_contacts'), payload, { merge: true });
}

export async function loadMailRecipientNames(
  crmClients: Array<Record<string, any> | null | undefined> = [],
  pendingApprovals: Array<Record<string, any> | null | undefined> = []
): Promise<Record<string, string>> {
  const names = collectCrmRecipientNames([...(crmClients || []), ...(pendingApprovals || [])]);
  try {
    const snap = await getDoc(doc(db, 'newsletter_system', 'global_contacts'));
    if (!snap.exists()) return names;
    const extras = parseExternalLeadNames(snap.data()?.externalLeadNames);
    for (const [email, name] of Object.entries(extras)) {
      if (!names[email]) names[email] = name;
    }
  } catch (error) {
    console.warn('loadMailRecipientNames:', error);
  }
  return names;
}
